import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { collection, doc, updateDoc, query, where, onSnapshot, addDoc, serverTimestamp } from "firebase/firestore"; // Adicionado addDoc e serverTimestamp
import { db, saveLog } from "../lib/firebase"; 

import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea"; 
import Navbar from "../components/ui/Navbar"; 
import AdminBar from "../components/ui/adminbar"; 
import { toast } from "sonner";

import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "../components/ui/dialog";

import {
  LayoutDashboard, Users, UserCog, UserCheck, ClipboardList, Wrench,
  FileText, Search, CheckCircle, XCircle, Eye, 
  Briefcase, ShieldCheck, ExternalLink, MessageSquareWarning, Loader2, MapPin, User, ShieldAlert, Phone,
  LifeBuoy
} from "lucide-react";

export default function AdminActTecs() {
  const navigate = useNavigate();
  const { userProfile, authLoading } = useAuth();
  const [loading, setLoading] = useState(true);
  const [btnLoading, setBtnLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [requests, setRequests] = useState([]);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectionReason, setRejectionReason] = useState("");
  const [techToReject, setTechToReject] = useState(null);

  // FUNÇÃO AUXILIAR PARA DISPARAR NOTIFICAÇÃO
  const sendNotification = async (targetUserId, title, message, type, category, link = "") => {
    try {
      await addDoc(collection(db, 'notifications'), {
        userId: targetUserId,
        title,
        message,
        type, // 'client' ou 'technician'
        category, // 'support', 'order', etc.
        link,
        read: false,
        createdAt: serverTimestamp()
      });
    } catch (error) {
      console.error("Erro ao enviar notificação:", error);
    }
  };

  useEffect(() => {
    const q = query(collection(db, "users"), where("technicianStatus", "==", "pending"));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      setRequests(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  const filteredRequests = useMemo(() => {
    return requests.filter(r => 
      (r.name || "").toLowerCase().includes(search.toLowerCase()) ||
      (r.professionalData?.name || "").toLowerCase().includes(search.toLowerCase())
    );
  }, [requests, search]);

  const handleApprove = async (tech) => {
    if (!window.confirm(`Aprovar ${tech.professionalData?.name || tech.name}?`)) return;
    setBtnLoading(true);
    try {
      await updateDoc(doc(db, "users", tech.id), {
        technicianStatus: 'approved',
        userType: 'technician',
        approvedAt: new Date().toISOString()
      });

      // NOTIFICAÇÃO PARA O TÉCNICO (AZUL - CATEGORIA SUPORTE)
      await sendNotification(
        tech.id, 
        "Cadastro Aprovado! 🚀", 
        "Seu perfil profissional foi aprovado. Agora você já pode receber chamados!", 
        "client", 
        "support", 
        "/profile-tech"
      );

      await saveLog(userProfile, "APROVAÇÃO TÉCNICA", `O administrador aprovou a solicitação de ${tech.professionalData?.name || tech.name}.`);

      toast.success("Técnico aprovado!");
      setSelectedRequest(null);
    } catch (e) { toast.error("Erro ao aprovar."); } 
    finally { setBtnLoading(false); }
  };

  const confirmReject = async () => {
    if (!rejectionReason.trim()) return toast.error("Descreva o motivo.");
    setBtnLoading(true);
    try {
      await updateDoc(doc(db, "users", techToReject.id), {
        technicianStatus: 'rejected',
        rejectionReason: rejectionReason,
        userType: 'client', 
        rejectedAt: new Date().toISOString()
      });

      // NOTIFICAÇÃO PARA O USUÁRIO (AZUL - CATEGORIA SUPORTE/AVISO)
      await sendNotification(
        techToReject.id, 
        "Solicitação Recusada", 
        `Infelizmente seu cadastro não foi aprovado. Motivo: ${rejectionReason}`, 
        "client", 
        "support", 
        "/become-technician"
      );

      await saveLog(userProfile, "REJEIÇÃO TÉCNICA", `O administrador rejeitou a solicitação de ${techToReject.professionalData?.name || techToReject.name}. Motivo: ${rejectionReason}`);

      toast.info("Solicitação rejeitada.");
      setShowRejectModal(false);
      setSelectedRequest(null);
    } catch (e) { toast.error("Erro ao rejeitar."); } 
    finally { setBtnLoading(false); }
  };

  // ... (Resto do seu código JSX permanece exatamente o mesmo)
  if (authLoading) return <div className="h-screen flex items-center justify-center font-black text-slate-400 uppercase text-left">Sincronizando...</div>;

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <Navbar />
      <div className="flex pt-[73px]">
        <AdminBar sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />

        <main className="flex-1 p-6 lg:p-10 max-w-7xl mx-auto w-full text-left">
          <header className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12 text-left">
            <div>
              <h1 className="text-4xl font-black text-slate-900 uppercase italic tracking-tighter leading-none">Aprovação <span className="text-orange-500">Técnica</span></h1>
              <p className="text-[10px] font-black text-slate-400 mt-2 flex items-center gap-2 animate-pulse">
                <div className="w-2 h-2 bg-green-500 rounded-full"></div> Monitoramento em tempo real
              </p>
            </div>
            <div className="relative w-full md:w-96 group">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-300" size={20} />
              <input 
                type="text" 
                placeholder="BUSCAR NOME OU EMPRESA..." 
                className="w-full h-14 pl-12 pr-6 bg-white border-none shadow-xl rounded-2xl font-bold text-sm uppercase italic outline-none focus:ring-2 focus:ring-orange-500/20 transition-all text-left" 
                value={search} 
                onChange={(e) => setSearch(e.target.value)} 
              />
            </div>
          </header>

          <div className="grid gap-4 text-left">
            {loading ? (
                <div className="py-20 text-center font-black text-slate-300 animate-pulse tracking-widest uppercase">Buscando solicitações...</div>
            ) : filteredRequests.length === 0 ? (
                <div className="py-20 text-center bg-white rounded-[3rem] border-2 border-dashed border-slate-200 uppercase font-black text-slate-300 italic">Nenhuma solicitação pendente.</div>
            ) : filteredRequests.map((req) => (
                <Card key={req.id} className={`rounded-[2rem] border-none shadow-xl transition-all overflow-hidden ${selectedRequest === req.id ? 'ring-2 ring-orange-500' : ''}`}>
                  <CardContent className="p-0">
                    <div className="p-6 flex flex-col md:flex-row items-center justify-between gap-6 text-left">
                      <div className="flex items-center gap-5">
                        <div className="w-14 h-14 rounded-2xl bg-orange-50 text-orange-600 flex items-center justify-center">
                          {req.professionalData?.photoUrl ? (
                            <img src={req.professionalData.photoUrl} className="w-full h-full object-cover rounded-2xl" alt="" />
                          ) : <Briefcase size={24} />}
                        </div>
                        <div className="text-left">
                          <h3 className="text-xl font-black text-slate-900 uppercase italic tracking-tighter">{req.professionalData?.name || "Pendente"}</h3>
                          <p className="text-[10px] font-bold text-slate-400 uppercase italic">VINCULADO A: {req.name}</p>
                        </div>
                      </div>
                      <Button 
                        onClick={() => setSelectedRequest(selectedRequest === req.id ? null : req.id)} 
                        className="h-12 rounded-xl font-black uppercase text-[10px] bg-slate-100 text-slate-600 hover:bg-slate-200 gap-2 px-6 shadow-sm transition-all"
                      >
                        {selectedRequest === req.id ? "RECOLHER" : "ANALISAR"} <Eye size={14}/>
                      </Button>
                    </div>

                    {selectedRequest === req.id && (
                      <div className="p-8 bg-white border-t border-slate-100 animate-in fade-in slide-in-from-top-2 duration-300 text-left">
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 text-left text-left">
                          <div className="space-y-6">
                             <div className="p-4 bg-slate-900 rounded-2xl text-white shadow-lg text-left">
                                <h4 className="text-[9px] font-black uppercase text-orange-500 mb-2 tracking-widest leading-none text-left">Dados de Cadastro</h4>
                                <p className="text-sm font-black uppercase italic">{req.name}</p>
                                <p className="text-[10px] font-bold text-slate-400 uppercase">{req.email}</p>
                             </div>

                             <h4 className="font-black uppercase italic text-orange-500 text-sm border-b pb-2 flex items-center gap-2"><FileText size={18}/> Detalhes</h4>
                             <div className="grid grid-cols-2 gap-4 text-sm font-bold text-left">
                                <div className="space-y-1"><Label className="text-[9px] uppercase text-slate-400 font-black">Comercial</Label><div className="p-3 bg-slate-50 rounded-xl border border-slate-100">{req.professionalData?.name}</div></div>
                                <div className="space-y-1"><Label className="text-[9px] uppercase text-slate-400 font-black">CPF/CNPJ</Label><div className="p-3 bg-slate-50 rounded-xl border border-slate-100">{req.professionalData?.document}</div></div>
                                
                                <div className="col-span-2 space-y-1 text-left">
                                    <Label className="text-[9px] uppercase text-blue-600 flex items-center gap-1 font-black italic"><Phone size={10}/> WhatsApp Contato</Label>
                                    <div className="p-3 bg-blue-50 border border-blue-100 rounded-xl text-blue-700 text-base">{req.professionalData?.phone || "Não informado"}</div>
                                </div>
                                
                                <div className="col-span-2 space-y-1 text-left text-left">
                                    <Label className="text-[9px] uppercase text-slate-400 flex items-center gap-1 font-black"><MapPin size={10}/> Local</Label>
                                    <div className="p-4 bg-orange-50/50 border border-orange-100 rounded-xl space-y-1 text-[11px]">
                                        <p className="font-bold text-slate-600 italic">Rua: {req.professionalData?.address?.street}, {req.professionalData?.address?.number}</p>
                                        <p className="font-bold text-slate-500 italic">Bairro: {req.professionalData?.address?.neighborhood}</p>
                                        <p className="font-black uppercase text-orange-600 italic">{req.professionalData?.address?.city} - {req.professionalData?.address?.state}</p>
                                    </div>
                                </div>
                             </div>
                          </div>

                          <div className="space-y-6 text-left">
                             <h4 className="font-black uppercase italic text-orange-500 text-sm border-b pb-2 flex items-center gap-2"><ExternalLink size={18}/> Arquivos Enviados</h4>
                             <div className="grid grid-cols-2 gap-4 text-left">
                                {[
                                    { label: 'FRENTE', url: req.verificationDocs?.documentFrontUrl },
                                    { label: 'VERSO', url: req.verificationDocs?.documentBackUrl },
                                    { label: 'SELFIE', url: req.verificationDocs?.selfieUrl },
                                    { label: 'ANTECEDENTES', url: req.verificationDocs?.criminalRecordUrl },
                                    { label: 'FOTO PERFIL', url: req.professionalData?.photoUrl }
                                ].map((f, i) => (
                                    <div key={i} className="group text-left text-left">
                                        <Label className="text-[8px] font-black text-slate-400 mb-1 block uppercase italic tracking-tighter text-left">{f.label}</Label>
                                        <div className="aspect-video bg-slate-100 rounded-xl overflow-hidden border-2 border-slate-50 relative cursor-pointer shadow-sm group-hover:border-orange-200 transition-all" onClick={() => f.url && window.open(f.url, '_blank')}>
                                            {f.url ? (
                                              <>
                                                <img src={f.url} className="w-full h-full object-cover group-hover:scale-105 transition-transform" alt="" />
                                                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 flex items-center justify-center text-white text-[10px] font-black uppercase">Abrir</div>
                                              </>
                                            ) : <div className="w-full h-full flex items-center justify-center text-slate-300 text-[10px] font-black uppercase">Vazio</div>}
                                        </div>
                                    </div>
                                ))}
                             </div>
                          </div>
                        </div>

                        <div className="flex flex-col md:flex-row items-center justify-center gap-4 pt-10 border-t mt-10">
                           <Button 
                             onClick={() => { setTechToReject(req); setRejectionReason(""); setShowRejectModal(true); }} 
                             disabled={btnLoading} 
                             className="w-full md:w-64 h-16 bg-red-50 text-red-600 hover:bg-red-600 hover:text-white font-black rounded-2xl uppercase italic text-xs gap-3 shadow-lg active:scale-95 transition-all"
                           >
                                <XCircle size={20} /> REJEITAR
                           </Button>
                           <Button 
                             onClick={() => handleApprove(req)} 
                             disabled={btnLoading} 
                             className="w-full md:w-64 h-16 bg-green-600 hover:bg-green-700 text-white font-black rounded-2xl uppercase italic text-xs gap-3 shadow-xl active:scale-95 transition-all"
                           >
                                {btnLoading ? <Loader2 className="animate-spin" /> : <><CheckCircle size={20} /> APROVAR</>}
                           </Button>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
            ))}
          </div>
        </main>
      </div>

      <Dialog open={showRejectModal} onOpenChange={setShowRejectModal}>
        <DialogContent className="rounded-[2.5rem] border-none p-10 max-w-2xl bg-white shadow-2xl outline-none text-left">
          <DialogHeader className="text-left text-left">
            <div className="w-16 h-16 bg-red-100 rounded-2xl flex items-center justify-center mb-6 shadow-inner text-red-600"><MessageSquareWarning size={32} /></div>
            <DialogTitle className="text-3xl font-black uppercase italic tracking-tighter text-slate-900 leading-none">Motivo da Rejeição</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-6 text-left">
            <Textarea 
              value={rejectionReason} 
              onChange={(e) => setRejectionReason(e.target.value)} 
              placeholder="Ex: A foto do documento está ilegível..." 
              className="min-h-[200px] rounded-3xl bg-slate-50 border-none font-bold italic text-slate-700 p-6 shadow-inner focus:ring-2 focus:ring-red-500/10 outline-none transition-all text-left" 
            />
          </div>
          <DialogFooter className="mt-4 flex flex-col md:flex-row gap-3">
            <Button onClick={confirmReject} disabled={btnLoading} className="w-full h-16 bg-red-600 hover:bg-red-700 text-white font-black rounded-2xl uppercase italic text-xs shadow-xl transition-all active:scale-95">CONFIRMAR REJEIÇÃO</Button>
            <Button onClick={() => setShowRejectModal(false)} variant="ghost" className="w-full h-12 font-black uppercase italic text-[10px] text-slate-400 hover:text-slate-900 transition-colors">CANCELAR</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}